import {createNewFolder, deleteFolder, getBuildPath, getMiniViewerPath} from "./utils";
import path from "path";
import {t} from "../../localization";
import {fs, fse, storage} from "../../electron-modules";
import {Asset, AssetType} from "../../redux/assets-slice";
import {
  moveAssetData,
  updateCameraDataState, updateCameraTransformPath,
  updateModelDataState,
  updateRawPointsState,
  updateStreamablePointsState
} from "./state";
import {isModelFile, isRawPointsFile, isStreamablePointsFile} from "../utils/misc";
import { cloneDeep } from "lodash";
import { PythonExecutable } from "../../executable";
import { ProjectType } from "../../types/project";


interface ExporterOpts {
  name: string;
  outputPath: string;
  assets: Asset[];
  singleFileOutput: boolean;
  state;
  onError?(title: string, message: string): void;
  onUpdate?(message: string): void;
  onLogs?(message: string): void;
  onProgress?(percent: number): void;
  onFinish?(): void;
  onCancel?(): void;
}

const exporterOptsDefaults = {
  onError: () => {},
  onUpdate: () => {},
  onLogs: () => {},
  onProgress: () => {},
  onFinish: () => {},
  onCancel: () => {}
};
const assetsFolder = "assets";

export const getStaticExporter = (): [(opts: ExporterOpts) => Promise<void>, () => Promise<void>] => {
  const exe = new PythonExecutable();
  const buildPath = getBuildPath();
  let cancelFlag = false;

  const cancel = async (webOutputPath) => {
    await deleteFolder(webOutputPath);
    cancelFlag = false;
  };

  const startExport = async (opts: ExporterOpts) => {
    opts = {...exporterOptsDefaults, ...opts};

    const rootOutputPath = path.join(opts.outputPath, opts.name);
    const webOutputPath = path.join(rootOutputPath, "web");
    const dataPath = path.join(webOutputPath, assetsFolder);

    opts.onUpdate(t('export.creating-output-folder'));
    await createNewFolder(rootOutputPath);
    await createNewFolder(webOutputPath);

    opts.onUpdate(t('export.copy-html-files-output-folder'));
    try {
      await fse.copy(buildPath, webOutputPath);
    } catch(err) {
      opts.onError(t("dialog.general-error-title"), t("dialog.export-error-static"));
      return;
    }

    opts.onUpdate(t('export.preparing-data-to-export'));
    await createNewFolder(dataPath);

    for (const [index, asset] of opts.assets.entries()) {
      opts.onUpdate(t("export.processing-data", {name: asset.name}));

      await moveAssetData(asset, webOutputPath, exe, opts.onLogs,
        opts.singleFileOutput, opts.onError);

      const percent = 100.0 * (index + 1) / opts.assets.length;
      opts.onProgress(percent);

      if (cancelFlag) {
        await cancel(webOutputPath);
        opts.onCancel();
        return;
      }
    }

    opts.onUpdate(t('export.export-project-state'));

    const state = cloneDeep(opts.state);

    // Update project type
    state.project.type = ProjectType.Static;

    opts.assets.forEach(asset => {
      if (isStreamablePointsFile(asset)) {
        updateStreamablePointsState(asset, state);
      } else if (isRawPointsFile(asset)) {
        updateRawPointsState(asset, state, opts.singleFileOutput);
      } else if (asset.type === AssetType.Panoramic || asset.type === AssetType.Planar) {
        updateCameraDataState(asset, state);
      } else if (isModelFile(asset)) {
        updateModelDataState(asset, state);
      }
    });

    // Update camera transform path
    updateCameraTransformPath(state, webOutputPath);

    const miniViewerPath = getMiniViewerPath();
    const exeOutputPath = path.join(rootOutputPath, path.basename(miniViewerPath));
    fs.copyFileSync(miniViewerPath, exeOutputPath);

    // Copy global settings
    const globalSettingsPath = path.join(
      storage.getDataPath(), "global-settings.json");

    const globalSettingsOutput = path.join(
      webOutputPath, "global-settings.json");

    if (fs.existsSync(globalSettingsPath)) {
      fs.copyFileSync(globalSettingsPath, globalSettingsOutput);
    }

    // Write final config file
    try {
      const projectJson = JSON.stringify(state, null, 2);
      const projectPath = path.join(webOutputPath, "static.json");
      await fse.writeFile(projectPath, projectJson);
    } catch (err) {
      opts.onError(t("dialog.general-error-title"), t("dialog.export-error-static"));
      return;
    }

    if (!cancelFlag) {
      opts.onFinish();
    } else {
      await cancel(webOutputPath);
      opts.onCancel();
    }
  };

  const cancelExport = async () => {
    cancelFlag = true;
    exe.destroy();
  };

  return [startExport, cancelExport];
};